/**
 * Predefined "smart" filters that compute dynamic filter tokens.
 * These are not persisted — they generate PropertyFilterGroups on the
 * fly based on the current date.
 */
import { PropertyFilterGroup } from "../searchSideBar/searchSideBarConnector";

export interface PredefinedFilter {
  readonly id: string;
  readonly label: string;
  /** Builds the filter group at call time (date-dependent). */
  readonly buildFilters: () => PropertyFilterGroup;
}

/** Start-of-day for `date` in the local timezone. */
function atStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** `date + days` (in whole days), preserving the time-of-day. */
function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Returns the Monday (start) and following Monday (exclusive upper
 * bound) of the current week as Date objects for Range filters.
 */
function getCurrentWeekRange(): { start: Date; end: Date } {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ...
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = atStartOfDay(addDays(now, diffToMonday));
  // Exclusive upper bound: next Monday covers all of Sunday.
  const nextMonday = atStartOfDay(addDays(monday, 7));

  return { start: monday, end: nextMonday };
}

/**
 * Returns today (start) and tomorrow (exclusive upper bound) as Date
 * objects for a Range filter covering the current calendar day.
 */
function getTodayRange(): { start: Date; end: Date } {
  const today = atStartOfDay(new Date());
  const tomorrow = atStartOfDay(addDays(today, 1));
  return { start: today, end: tomorrow };
}

/**
 * Returns last Monday (start) and this Monday (exclusive upper
 * bound), so the range covers the entire previous Mon–Sun window.
 */
function getLastWeekRange(): { start: Date; end: Date } {
  const { start: thisMonday } = getCurrentWeekRange();
  const lastMonday = atStartOfDay(addDays(thisMonday, -7));
  return { start: lastMonday, end: thisMonday };
}

/**
 * Wraps a `{ lower, upper }` date range as an `announceDate`
 * PropertyFilterGroup. Mirrors the shape produced by the date-range
 * UI: sumPropertyFilterQueries routes non-string values through
 * datesToQuery, which emits a `range(...)` Lisp query.
 */
function announceDateRangeFilter(range: {
  start: Date;
  end: Date;
}): PropertyFilterGroup {
  return {
    type: "PropertyFilterGroup",
    operation: "and",
    filters: [
      {
        type: "PropertyFilterGroup",
        operation: "and",
        filters: [
          {
            type: "PropertyFilter",
            propertyKey: "announceDate",
            operator: "filter",
            value: { lower: range.start, upper: range.end },
          },
        ],
      },
    ],
  };
}

/** "Today's Launches" — ASINs with an Announce Date of today. */
const todaysLaunches: PredefinedFilter = {
  id: "todays-launches",
  label: "Today's Launches",
  buildFilters: () => announceDateRangeFilter(getTodayRange()),
};

/**
 * "This Week's Launches" — ASINs whose Announce Date falls within
 * the current Monday–Sunday window.
 */
const thisWeeksLaunches: PredefinedFilter = {
  id: "this-weeks-launches",
  label: "This Week's Launches",
  buildFilters: () => announceDateRangeFilter(getCurrentWeekRange()),
};

/**
 * "Last Week's Launches" — ASINs whose Announce Date falls within
 * the previous Monday–Sunday window.
 */
const lastWeeksLaunches: PredefinedFilter = {
  id: "last-weeks-launches",
  label: "Last Week's Launches",
  buildFilters: () => announceDateRangeFilter(getLastWeekRange()),
};

/** All predefined filters, in display order. */
export const PREDEFINED_FILTERS: readonly PredefinedFilter[] = [
  todaysLaunches,
  thisWeeksLaunches,
  lastWeeksLaunches,
];
